"use client";

import Link from "next/link";
import { ChevronRight, Compass, GraduationCap } from "lucide-react";

import { getContentMeta } from "@/components/learning-paths/contentMeta";
import { isManifestPagePath } from "@/lib/navigation/manifest-hrefs";
import { cn } from "@/lib/utils";

// Learning paths aren't a CONTENT_META content type (they contain content), so
// they get their own identity — blue (the RESOURCE color family) + the same
// GraduationCap the home "Continue learning" list uses.
const LEARNING_PATH_META = {
  icon: GraduationCap,
  tile: "bg-blue-50 text-blue-600",
};

// Static app pages (reports, playbook, admin, …) are wayfinding rather than
// content, so they read as "go here" instead of the unknown-content fallback.
const APP_PAGE_META = {
  icon: Compass,
  tile: "bg-gray-100 text-gray-600",
};

/**
 * Resolve a link's icon + tint from the learning-path content-type system
 * (`contentMeta`) so Sabine's action links match the rest of the app:
 * roleplay = violet, test = emerald, FAQ = teal, glossary = indigo.
 */
function metaForHref(href = "") {
  if (href.includes("/dashboard/role-plays/assignments/")) {
    return getContentMeta("ROLEPLAY");
  }
  if (href.includes("/faq-tests/assignments/")) {
    return getContentMeta("FAQ");
  }
  if (href.includes("/glossary-tests/assignments/")) {
    return getContentMeta("GLOSSARY");
  }
  if (href.includes("/tests/assignments/")) {
    return getContentMeta("TEST");
  }
  if (
    href.includes("/dashboard/learning-paths") ||
    href.includes("/learning-paths/")
  ) {
    return LEARNING_PATH_META;
  }
  if (isManifestPagePath(href)) {
    return APP_PAGE_META;
  }
  return getContentMeta(); // unknown → neutral gray fallback
}

export default function AgentActionLink({
  href,
  children,
  className,
  labelPrefix = "Open",
}) {
  const meta = metaForHref(href);
  const Icon = meta.icon;
  const label = String(children || "Open item").trim();

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${labelPrefix} ${label}`}
      title={label}
      className={cn(
        "group inline-flex min-h-9 max-w-full items-center gap-2.5 rounded-xl border border-gray-200 bg-white py-1.5 pl-1.5 pr-3 transition-all duration-200 hover:-translate-y-px hover:border-gray-300 hover:bg-gray-50/60",
        className
      )}
    >
      <span
        className={cn(
          "grid h-6 w-6 shrink-0 place-items-center rounded-lg transition-transform duration-200 group-hover:scale-105",
          meta.tile
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-800 transition-colors group-hover:text-gray-900">
        {label}
      </span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-400" />
    </Link>
  );
}
